import { ITool, IToolBag, IToolBagRequest } from '../utils/entities/interface';
import { del, get, post, put } from './https'; // Make sure to adjust the import for the `get` function

interface Pagination {
  totalUsers: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ToolListResponse {
  status: boolean;
  data: ITool[];
  count: number;
  pagination: Pagination;
}

const GetTools = async ({
  page = 1,
  limit = 1000,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<ToolListResponse> => {
  let url = `/api/v1/admin/tools/list?limit=${limit}&page=${page}`;
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  const response = await get(url);

  if (response.status == 200) {
    return response.data;
  } else {
    throw new Error('Failed to fetch users');
  }
};

const CreateTool = async ({
  name,
  description,
  image = 'https://example.com/images/sample-image.jpg',
}: {
  name: string;
  description: string;
  image?: string;
}): Promise<number> => {
  const payload = {
    name,
    description,
    image,
  };

  const response = await post('/api/v1/admin/tools/create', payload);

  if (response.status === 201) {
    return 201;
  } else {
    throw new Error(response.statusText || 'Failed to create tool');
  }
};

interface ToolBagListResponse {
  status: boolean;
  data: IToolBag[];
  count: number;
  pagination: Pagination;
}
const GetToolBags = async ({
  page = 1,
  limit = 1000,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<ToolBagListResponse> => {
  let url = `/api/v1/admin/tool-bag/list?limit=${limit}&page=${page}`;
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  const response = await get(url);

  if (response.status == 200) {
    return response.data;
  } else {
    throw new Error('Failed to fetch users');
  }
};
const CreateToolBag = async ({
  name,
  description,
  tools = [],
}: {
  name: string;
  description: string;
  tools?: any[];
}): Promise<number> => {
  const payload = {
    name,
    description,
    tools,
  };
  const response = await post('/api/v1/admin/tool-bag/create', payload);
  if (response.status === 201) {
    return 201;
  } else {
    throw new Error(response.statusText || 'Failed to create tool');
  }
};

const EditToolBag = async ({
  toolBagId,
  name,
  description,
}: {
  toolBagId: string;
  name: string;
  description: string;
}): Promise<number> => {
  const url = `/api/v1/admin/tool-bag/edit/${toolBagId}`;
  const payload = {
    name,
    description,
    toolBagId,
  };
  const response = await put(url, payload);

  console.log('Edit Tool Bag Response:', response);
  if (response.status) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to edit tool-bag');
  }
};

const ModifyToolBag = async ({
  toolBagId,
  toolId,
  name,
  quantity,
  description,
  action,
}: {
  toolBagId: string;
  toolId: string;
  name?: string;
  quantity?: number;
  description?: string;
  action: 'add' | 'remove';
}): Promise<number> => {
  const response = await post('/api/v1/admin/tool-bag/modify-tool', {
    toolBagId,
    toolId,
    name,
    quantity,
    description,
    action,
  });
  if (response.status === 200) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to modify tool-bag');
  }
};

const DeleteToolBag = async (toolBagId: string): Promise<number> => {
  const url = `/api/v1/admin/tool-bag/delete/${toolBagId}`;
  const response = await del(url);

  console.log('Delete Tool Bag Response:', response);
  if (response.status) {
    return 200;
  } else {
    throw new Error('Failed to delete tool-bag');
  }
};

const DeleteTool = async (toolId: string): Promise<number> => {
  const url = `/api/v1/admin/tools/delete/${toolId}`;
  const response = await del(url);

  console.log('Delete Tool Response:', response);
  if (response.status) {
    return 200;
  } else {
    throw new Error('Failed to delete tool');
  }
};

const RequestToolBag = async ({
  technicianId,
}: {
  technicianId: string;
}): Promise<number> => {
  const response = await post(`/api/v1/admin/toolbag-requests/${technicianId}`);
  if (response.status === 201) {
    return 201;
  } else {
    throw new Error(response.statusText || 'Failed to request toolbag');
  }
};

const GetToolBagsRequests = async (): Promise<IToolBagRequest[]> => {
  const url = `/api/v1/admin/toolbag-requests`;
  const response = await get(url);

  console.log('Get Tool Bag Requests Response:', response);
  if (response.status === 200 && response.data) {
    return response.data.data as IToolBagRequest[];
  } else {
    throw new Error('Failed to fetch toolbag requests');
  }
};

export const AssignToolBag = async (
  requestId: string,
  assignedToolBagId: string,
): Promise<number> => {
  const response = await put(`/api/v1/admin/toolbag-requests/${requestId}`, {
    assignedToolBagId,
  });

  console.log('Assign Tool Bag Response:', response);
  if (response.status) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to assign toolbag');
  }
};

const TechnicianTools = async ({
  technicianId,
}: {
  technicianId: string;
}): Promise<Array<any>> => {
  const response = await get(
    `/api/v1/admin/technician/assigned/tools?technicianId=${technicianId}`,
  );
  if (response.status === 200 && response.data) {
    return response.data.data as Array<any>;
  } else {
    throw new Error('Failed to fetch toolbag requests');
  }
};

export {
  GetTools,
  CreateTool,
  GetToolBags,
  CreateToolBag,
  ModifyToolBag,
  DeleteToolBag,
  DeleteTool,
  EditToolBag,
  RequestToolBag,
  GetToolBagsRequests,
  TechnicianTools,
};
